import React, { useState } from 'react';
import { useNavigate, useLocation, useParams } from 'react-router-dom';
import { useAuth } from '../../context/AuthContext';
import { Button } from '../ui/button';
import { LogOut, Home, User, HelpCircle, FileText, Calculator, GraduationCap, Building2, Gift, Sparkles, Crown, ChevronDown, ChevronRight, Receipt, ExternalLink, Atom, Mic2, Search, Video } from 'lucide-react';
import { Badge } from '../ui/badge';
import BetaFoundersModal from '../BetaFoundersModal';

const LOGO_URL = "https://customer-assets.emergentagent.com/job_61ca5ff4-bcc2-4cdc-8453-7407936ace18/artifacts/6nklllpo_TaxSetu_Only_Logo_Design_Infinite-removebg-preview.png";

// Searchable catalogue of simulators (used by navbar search). Status: 'live' | 'soon'.
const SEARCHABLE_SIMS = [
  { id: 'gst',         name: 'GST Simulator',                tags: 'gst registration gstr1 gstr3b hsn',          route: '/gst',                  status: 'live' },
  { id: 'gst-reg',     name: 'GST Registration Practice',    tags: 'gst registration new application',           route: '/gst/registration',     status: 'live' },
  { id: 'gstr1',       name: 'GSTR-1 Filing',                tags: 'gstr1 b2b sales outward supply',              route: '/gst/gstr1',            status: 'live' },
  { id: 'gstr3b',      name: 'GSTR-3B Filing',               tags: 'gstr3b summary monthly return',               route: '/gst/gstr3b',           status: 'live' },
  { id: 'tds',         name: 'TDS Return Simulator',         tags: 'tds 24q 26q 27q form deduction',              route: '/income-tax/tds',       status: 'live' },
  { id: 'finance',     name: 'Finance Ratios Simulator',     tags: 'finance ratios pnl balance sheet roi roe roa', route: '/finance/ratios',      status: 'live' },
  { id: 'itr',         name: 'Income Tax Return Simulator',  tags: 'itr income tax return form-16',               route: null,                    status: 'soon' },
  { id: 'class-9',     name: 'Class 9 Science',              tags: 'class 9 science ncert physics chemistry',     route: '/science/class-9',      status: 'live' },
  { id: 'class-10',    name: 'Class 10 Science',             tags: 'class 10 science ncert',                       route: null,                    status: 'soon' },
  { id: 'class-11',    name: 'Class 11 Science',             tags: 'class 11 science',                             route: null,                    status: 'soon' },
  { id: 'class-12',    name: 'Class 12 Science',             tags: 'class 12 science',                             route: null,                    status: 'soon' },
  { id: 'interview',   name: 'AI Interview Simulator',       tags: 'interview mock ai voice hr',                   route: null,                    status: 'soon' },
];

// Mega-menu item card (used in Simulators dropdown)
const SimItem = ({ onClick, disabled = false, icon: Icon, iconWrap, iconColor, title, subtitle, badge, testId }) => {
  const isLive = badge?.variant === 'live';
  return (
    <button
      type="button"
      onClick={disabled ? undefined : onClick}
      disabled={disabled}
      data-testid={testId}
      className={`text-left p-3.5 rounded-xl border bg-white/[0.02] transition-all group ${
        disabled
          ? 'opacity-50 cursor-not-allowed border-white/5'
          : 'hover:bg-white/[0.06] border-white/5 hover:border-white/15'
      }`}
    >
      <div className="flex items-start justify-between mb-2">
        <div className={`w-9 h-9 rounded-lg bg-gradient-to-br ${iconWrap} border flex items-center justify-center`}>
          <Icon className={`w-4 h-4 ${iconColor}`} />
        </div>
        {badge && (
          <Badge
            className={`text-[10px] ${
              isLive
                ? 'bg-emerald-500/15 text-emerald-400 border-emerald-500/30'
                : 'bg-amber-500/15 text-amber-400 border-amber-500/30'
            }`}
          >
            {badge.text}
          </Badge>
        )}
      </div>
      <h4 className={`text-sm font-semibold ${disabled ? 'text-gray-300' : 'text-white group-hover:text-teal-300'} transition-colors`}>{title}</h4>
      <p className="text-[11px] text-gray-500 mt-1 leading-snug">{subtitle}</p>
    </button>
  );
};

const Header = ({ organization }) => {
  const navigate = useNavigate();
  const location = useLocation();
  const { slug } = useParams();
  const { isLoggedIn, user, logout, isAdmin, isGstr1Paid, isGstr3bPaid } = useAuth();
  const [showBetaModal, setShowBetaModal] = useState(false);
  const [showUserMenu, setShowUserMenu] = useState(false);
  const [showIncomeTaxMenu, setShowIncomeTaxMenu] = useState(false);
  const [activeSimCategory, setActiveSimCategory] = useState('tax');
  const [searchQuery, setSearchQuery] = useState('');
  const [showSearchResults, setShowSearchResults] = useState(false);

  // Check if modules are unlocked (via any method)
  const hasUnlockedModules = isAdmin || isGstr1Paid || isGstr3bPaid;

  // Get lead name from localStorage
  const leadName = typeof window !== 'undefined' ? localStorage.getItem('practiq_lead_name') : null;
  const leadEmail = typeof window !== 'undefined' ? localStorage.getItem('practiq_lead_email') : null;

  // Check if we're on an org route
  const isOrgRoute = slug && organization;
  const basePath = isOrgRoute ? `/${slug}` : '';

  const handleLogout = () => {
    logout();
    // Also clear lead data
    localStorage.removeItem('practiq_lead_email');
    localStorage.removeItem('practiq_lead_name');
    localStorage.removeItem('practiq_lead_firstName');
    localStorage.removeItem('practiq_registration_session');
    localStorage.removeItem('practiq_mock_inbox');
    navigate(isOrgRoute ? `/${slug}/login` : '/');
  };

  // If on org route, show co-branded header
  if (isOrgRoute) {
    return (
      <header className="w-full bg-[#0A0F1C] text-white">
        {/* Co-branded Top Bar */}
        <div className="w-full bg-gradient-to-r from-teal-900/20 via-[#12192D] to-teal-900/20 border-b border-teal-500/20">
          <div className="w-full px-6 lg:px-16 xl:px-24 py-2.5">
            <div className="flex items-center justify-between">
              <div className="flex items-center gap-2">
                <Building2 className="w-4 h-4 text-teal-400" />
                <span className="text-sm text-gray-300">
                  <span className="text-white font-medium">{organization.name}</span>
                  <span className="mx-2 text-gray-600">×</span>
                  <span className="text-teal-400">PractiQ Partner Portal</span>
                </span>
              </div>
              <Badge className="bg-amber-500/20 text-amber-400 border-amber-500/30">
                Institutional Access
              </Badge>
            </div>
          </div>
        </div>
        
        {/* Main Header with Co-branding */}
        <div className="w-full px-6 lg:px-16 xl:px-24 py-5 border-b border-white/5">
          <div className="flex items-center justify-between">
            {/* Co-branded Logo Section */}
            <div className="flex items-center gap-4">
              {/* PractiQ Logo */}
              <div 
                className="flex items-center gap-3 cursor-pointer group"
                onClick={() => navigate(basePath || '/')}
              >
                <img 
                  src={LOGO_URL} 
                  alt="PractiQ Logo" 
                  className="w-10 h-10 object-contain"
                />
                <span className="text-xl font-semibold text-white">PractiQ</span>
              </div>
              
              {/* X separator */}
              <span className="text-2xl text-gray-600 font-light">×</span>
              
              {/* Organization Logo/Name */}
              {organization.logo_url ? (
                <img 
                  src={organization.logo_url} 
                  alt={organization.name} 
                  className="h-10 object-contain"
                />
              ) : (
                <div className="flex items-center gap-2">
                  <Building2 className="w-8 h-8" style={{ color: organization.primary_color || '#14b8a6' }} />
                  <span className="text-xl font-semibold text-white">{organization.name}</span>
                </div>
              )}
            </div>

            {/* Navigation */}
            <nav className="hidden md:flex items-center gap-2">
              <button 
                onClick={() => navigate(`${basePath}/gst`)}
                className={`flex items-center gap-2 px-5 py-2.5 rounded-lg text-base transition-all ${location.pathname.includes('/gst') ? 'text-white bg-white/5 border border-teal-500/30' : 'text-gray-400 hover:text-white hover:bg-white/5'}`}
              >
                <FileText className="w-5 h-5" />
                <span>GST Simulator</span>
              </button>
            </nav>

            {/* User Section */}
            <div className="flex items-center gap-4">
              {isLoggedIn ? (
                <div className="flex items-center gap-4">
                  <div className="hidden sm:flex items-center gap-2 bg-white/5 px-4 py-2.5 rounded-lg border border-white/10">
                    <User className="w-5 h-5 text-teal-400" />
                    <span className="text-base text-gray-300">{user?.email || user?.username}</span>
                  </div>
                  <Button
                    variant="outline"
                    size="default"
                    onClick={handleLogout}
                    className="border-white/10 text-gray-300 hover:bg-white/5 hover:text-white hover:border-white/20 h-10"
                  >
                    <LogOut className="w-5 h-5 mr-2" />
                    Logout
                  </Button>
                </div>
              ) : (
                <Button
                  onClick={() => navigate(`${basePath}/login`)}
                  className="text-white border-0 shadow-lg shadow-teal-500/20 h-11 px-6 text-base"
                  style={{ background: `linear-gradient(to right, ${organization.primary_color || '#14b8a6'}, ${organization.primary_color || '#14b8a6'}cc)` }}
                >
                  <User className="w-5 h-5 mr-2" />
                  Login
                </Button>
              )}
            </div>
          </div>
        </div>
      </header>
    );
  }

  // Default B2C Header
  return (
    <header className="w-full bg-[#0A0F1C] text-white border-b border-white/5">
      {/* Main Header */}
      <div className="w-full px-6 lg:px-10 xl:px-14 py-4">
        <div className="flex items-center justify-between gap-4">
          {/* Logo Section */}
          <div className="flex items-center gap-3 flex-shrink-0">
            <div 
              className="relative cursor-pointer group"
              onClick={() => navigate('/')}
            >
              <div className="absolute inset-0 bg-gradient-to-r from-teal-500 to-cyan-500 rounded-xl blur-lg opacity-30 group-hover:opacity-50 transition-opacity"></div>
              <img 
                src={LOGO_URL} 
                alt="PractiQ Logo" 
                className="w-12 h-12 object-contain relative z-10"
              />
            </div>
            <div className="min-w-0">
              <div className="flex items-baseline gap-2 whitespace-nowrap">
                <h1 
                  className="text-2xl font-semibold tracking-tight cursor-pointer leading-none"
                  onClick={() => navigate('/')}
                >
                  <span className="bg-gradient-to-r from-teal-400 to-cyan-400 bg-clip-text text-transparent">PractiQ</span>
                </h1>
                <a
                  href="https://www.taxsetu.ai"
                  target="_blank"
                  rel="noopener noreferrer"
                  className="text-sm text-gray-500 hover:text-teal-400 transition-colors inline-flex items-center gap-1 whitespace-nowrap"
                  data-testid="logo-by-taxsetu"
                >
                  by <span className="text-gray-400 font-medium">TaxSetu AI</span>
                  <ExternalLink className="w-3 h-3 opacity-60" />
                </a>
              </div>
              <p className="text-[10px] text-gray-600 tracking-widest uppercase mt-1 font-medium whitespace-nowrap">EdTech AI Simulation Platform</p>
            </div>
          </div>

          {/* Navigation + User Section - All on Right */}
          <div className="hidden md:flex items-center gap-1.5 lg:gap-2 flex-shrink-0">
            {/* PW-style Search Bar */}
            <div className="relative hidden lg:block">
              <div className="flex items-center gap-2 bg-white/[0.04] border border-white/10 rounded-full px-3 py-1.5 w-56 hover:border-white/20 focus-within:border-teal-400/60 focus-within:bg-white/[0.06] transition-all">
                <Search className="w-4 h-4 text-gray-400 flex-shrink-0" />
                <input
                  type="text"
                  value={searchQuery}
                  onChange={(e) => { setSearchQuery(e.target.value); setShowSearchResults(true); }}
                  onFocus={() => setShowSearchResults(true)}
                  placeholder="Search simulators..."
                  data-testid="navbar-search-input"
                  className="bg-transparent text-sm text-white placeholder-gray-500 outline-none w-full"
                />
              </div>
              {showSearchResults && searchQuery.trim().length > 0 && (() => {
                const q = searchQuery.trim().toLowerCase();
                const matches = SEARCHABLE_SIMS.filter(s =>
                  s.name.toLowerCase().includes(q) || s.tags.toLowerCase().includes(q)
                ).slice(0, 6);
                return (
                  <>
                    <div
                      className="fixed inset-0 z-40"
                      onClick={() => setShowSearchResults(false)}
                    />
                    <div className="absolute left-0 top-full mt-2 w-72 bg-[#0F1626] border border-white/10 rounded-xl shadow-2xl z-50 overflow-hidden" data-testid="navbar-search-results">
                      {matches.length === 0 ? (
                        <p className="px-4 py-3 text-sm text-gray-500">No simulators match &quot;{searchQuery}&quot;.</p>
                      ) : (
                        matches.map(m => (
                          <button
                            key={m.id}
                            type="button"
                            disabled={!m.route}
                            onClick={() => {
                              if (m.route) {
                                setShowSearchResults(false);
                                setSearchQuery('');
                                navigate(m.route);
                              }
                            }}
                            data-testid={`navbar-search-result-${m.id}`}
                            className={`w-full flex items-center justify-between gap-3 px-4 py-2.5 text-left transition-colors ${
                              m.route ? 'hover:bg-white/[0.05] cursor-pointer' : 'opacity-50 cursor-not-allowed'
                            } border-b border-white/5 last:border-b-0`}
                          >
                            <span className="text-sm text-white truncate">{m.name}</span>
                            <Badge className={`text-[10px] ${
                              m.status === 'live'
                                ? 'bg-emerald-500/15 text-emerald-400 border-emerald-500/30'
                                : 'bg-amber-500/15 text-amber-400 border-amber-500/30'
                            }`}>
                              {m.status === 'live' ? 'Live' : 'Soon'}
                            </Badge>
                          </button>
                        ))
                      )}
                    </div>
                  </>
                );
              })()}
            </div>

            {/* Nav Items */}
            <button 
              onClick={() => navigate('/')}
              className={`flex items-center gap-2 px-3 py-2 rounded-lg text-sm whitespace-nowrap transition-all ${location.pathname === '/' ? 'text-white bg-white/10' : 'text-gray-400 hover:text-white hover:bg-white/5'}`}
            >
              <Home className="w-4 h-4" />
              <span>Home</span>
            </button>

            {/* Simulators Mega-Menu (PW-style: left categories + right items) */}
            <div className="relative">
              <button 
                onClick={() => setShowIncomeTaxMenu(!showIncomeTaxMenu)}
                className={`flex items-center gap-2 px-3 py-2 rounded-lg text-sm whitespace-nowrap transition-all ${
                  (location.pathname.startsWith('/gst') || location.pathname.startsWith('/income-tax'))
                    ? 'text-white bg-white/10 border border-teal-500/30'
                    : 'text-gray-400 hover:text-white hover:bg-white/5'
                }`}
                data-testid="simulators-menu-trigger"
              >
                <Sparkles className="w-4 h-4 text-teal-400" />
                <span>Simulators</span>
                <ChevronDown className={`w-4 h-4 transition-transform ${showIncomeTaxMenu ? 'rotate-180' : ''}`} />
              </button>
              
              {showIncomeTaxMenu && (
                <>
                  <div 
                    className="fixed inset-0 z-40" 
                    onClick={() => setShowIncomeTaxMenu(false)}
                  />
                  <div
                    className="absolute right-0 top-full mt-3 w-[min(760px,calc(100vw-2rem))] bg-[#0F1626] border border-white/10 rounded-2xl shadow-2xl z-50 overflow-hidden"
                    data-testid="simulators-menu"
                  >
                    <div className="grid grid-cols-12 min-h-[380px]">
                      {/* LEFT — Category rail */}
                      <div className="col-span-4 bg-white/[0.02] border-r border-white/5 py-3">
                        {[
                          { id: 'tax',      label: 'Tax Simulator',       sub: 'GST · TDS · ITR',          icon: Calculator,    color: 'text-teal-400' },
                          { id: 'finance',  label: 'Finance Simulator',   sub: 'Ratios · P&L · Balance',   icon: Sparkles,      color: 'text-emerald-400' },
                          { id: 'science',  label: 'Class 9 to 12',       sub: 'Science · NCERT',          icon: GraduationCap, color: 'text-cyan-400' },
                          { id: 'interview',label: 'AI Interview',        sub: 'Voice-based mock interview',icon: Mic2,         color: 'text-purple-400' },
                        ].map((cat) => {
                          const Icon = cat.icon;
                          const isActive = activeSimCategory === cat.id;
                          return (
                            <button
                              key={cat.id}
                              type="button"
                              onMouseEnter={() => setActiveSimCategory(cat.id)}
                              onClick={() => setActiveSimCategory(cat.id)}
                              data-testid={`sim-cat-${cat.id}`}
                              className={`w-full text-left flex items-center gap-3 px-5 py-3 transition-colors ${
                                isActive
                                  ? 'bg-gradient-to-r from-teal-500/10 to-transparent border-l-2 border-teal-400'
                                  : 'border-l-2 border-transparent hover:bg-white/[0.03]'
                              }`}
                            >
                              <div className={`w-9 h-9 rounded-lg bg-white/[0.04] border border-white/10 flex items-center justify-center flex-shrink-0`}>
                                <Icon className={`w-4 h-4 ${cat.color}`} />
                              </div>
                              <div className="flex-1 min-w-0">
                                <p className={`text-sm font-medium ${isActive ? 'text-white' : 'text-gray-300'}`}>{cat.label}</p>
                                <p className="text-[11px] text-gray-500 truncate">{cat.sub}</p>
                              </div>
                              <ChevronRight className={`w-4 h-4 flex-shrink-0 transition-colors ${isActive ? 'text-teal-400' : 'text-gray-600'}`} />
                            </button>
                          );
                        })}
                      </div>

                      {/* RIGHT — Items pane */}
                      <div className="col-span-8 p-5">
                        <p className="text-[11px] text-gray-500 tracking-widest uppercase font-medium mb-3">
                          {activeSimCategory === 'tax' && 'Tax Filing Simulators'}
                          {activeSimCategory === 'finance' && 'Finance · Practice Simulators'}
                          {activeSimCategory === 'science' && 'Class 9–12 Science · NCERT'}
                          {activeSimCategory === 'interview' && 'AI Interview · Voice Mock'}
                        </p>

                        {/* TAX SIMULATOR items */}
                        {activeSimCategory === 'tax' && (
                          <div className="grid grid-cols-2 gap-3">
                            <SimItem
                              onClick={() => { setShowIncomeTaxMenu(false); navigate('/gst'); }}
                              icon={FileText}
                              iconWrap="from-teal-500/20 to-cyan-500/20 border-teal-500/30"
                              iconColor="text-teal-400"
                              title="GST Simulator"
                              subtitle="Registration · GSTR-1 · GSTR-3B · HSN"
                              badge={{ text: 'Live', variant: 'live' }}
                              testId="sim-gst"
                            />
                            <SimItem
                              onClick={() => { setShowIncomeTaxMenu(false); navigate('/income-tax/tds'); }}
                              icon={Receipt}
                              iconWrap="from-purple-500/20 to-pink-500/20 border-purple-500/30"
                              iconColor="text-purple-400"
                              title="TDS Return Simulator"
                              subtitle="Form 24Q · 26Q · 27Q filing"
                              badge={{ text: 'Live', variant: 'live' }}
                              testId="sim-tds"
                            />
                            <SimItem
                              disabled
                              icon={Calculator}
                              iconWrap="from-indigo-500/15 to-blue-500/15 border-indigo-500/20"
                              iconColor="text-indigo-400"
                              title="Income Tax Return Simulator"
                              subtitle="ITR-1 to ITR-7 · Form 16 parsing"
                              badge={{ text: 'Coming Soon', variant: 'soon' }}
                              testId="sim-itr"
                            />
                            <SimItem
                              disabled
                              icon={Building2}
                              iconWrap="from-rose-500/15 to-fuchsia-500/15 border-rose-500/20"
                              iconColor="text-rose-400"
                              title="Company Incorporation"
                              subtitle="Pvt Ltd · LLP · OPC — SPICe+ / FiLLiP"
                              badge={{ text: 'Coming Soon', variant: 'soon' }}
                              testId="sim-incorporation"
                            />
                          </div>
                        )}

                        {/* FINANCE SIMULATOR items */}
                        {activeSimCategory === 'finance' && (
                          <div className="grid grid-cols-2 gap-3">
                            <SimItem
                              onClick={() => { setShowIncomeTaxMenu(false); navigate('/finance/ratios'); }}
                              icon={Sparkles}
                              iconWrap="from-emerald-500/20 to-teal-500/20 border-emerald-500/30"
                              iconColor="text-emerald-400"
                              title="Financial Ratios"
                              subtitle="P&L · Balance · 9 live ratios + Health Score"
                              badge={{ text: 'Live', variant: 'live' }}
                              testId="sim-finance-ratios"
                            />
                            <SimItem
                              disabled
                              icon={Calculator}
                              iconWrap="from-amber-500/15 to-orange-500/15 border-amber-500/20"
                              iconColor="text-amber-400"
                              title="Break-even Analysis"
                              subtitle="Fixed · Variable · BEP units"
                              badge={{ text: 'Coming Soon', variant: 'soon' }}
                              testId="sim-finance-bep"
                            />
                            <SimItem
                              disabled
                              icon={Calculator}
                              iconWrap="from-indigo-500/15 to-blue-500/15 border-indigo-500/20"
                              iconColor="text-indigo-400"
                              title="DCF Valuation"
                              subtitle="Free cash flow · WACC · Terminal value"
                              badge={{ text: 'Coming Soon', variant: 'soon' }}
                              testId="sim-finance-dcf"
                            />
                            <SimItem
                              disabled
                              icon={Calculator}
                              iconWrap="from-rose-500/15 to-fuchsia-500/15 border-rose-500/20"
                              iconColor="text-rose-400"
                              title="Loan EMI &amp; Amortization"
                              subtitle="Principal · Rate · Tenure · Schedule"
                              badge={{ text: 'Coming Soon', variant: 'soon' }}
                              testId="sim-finance-emi"
                            />
                          </div>
                        )}

                        {/* CLASS 9-12 SCIENCE items */}
                        {activeSimCategory === 'science' && (
                          <div className="grid grid-cols-2 gap-3">
                            <SimItem
                              onClick={() => { setShowIncomeTaxMenu(false); navigate('/science/class-9'); }}
                              icon={Atom}
                              iconWrap="from-teal-500/20 to-cyan-500/20 border-teal-500/30"
                              iconColor="text-teal-400"
                              title="Class 9 Science"
                              subtitle="8 chapters · 2 sims live"
                              badge={{ text: 'Live', variant: 'live' }}
                              testId="sim-science-class-9"
                            />
                            {[10, 11, 12].map((cls) => (
                              <SimItem
                                key={cls}
                                disabled
                                icon={GraduationCap}
                                iconWrap="from-indigo-500/15 to-blue-500/15 border-indigo-500/20"
                                iconColor="text-indigo-400"
                                title={`Class ${cls} Science`}
                                subtitle="Physics · Chemistry · Biology"
                                badge={{ text: 'Coming Soon', variant: 'soon' }}
                                testId={`sim-science-class-${cls}`}
                              />
                            ))}
                          </div>
                        )}

                        {/* AI INTERVIEW item */}
                        {activeSimCategory === 'interview' && (
                          <div className="grid grid-cols-2 gap-3">
                            <SimItem
                              disabled
                              icon={Mic2}
                              iconWrap="from-purple-500/20 to-pink-500/20 border-purple-500/30"
                              iconColor="text-purple-400"
                              title="AI Interview Simulator"
                              subtitle="3 interviewers · Voice-based mock"
                              badge={{ text: 'Coming Soon', variant: 'soon' }}
                              testId="sim-interview"
                            />
                          </div>
                        )}
                      </div>
                    </div>

                    {/* Footer strip */}
                    <div className="px-5 py-3 border-t border-white/5 bg-white/[0.02] flex items-center justify-between">
                      <p className="text-[11px] text-gray-500">
                        India&apos;s risk-free practice environment for CA, Tax &amp; Finance pros.
                      </p>
                      <button
                        onClick={() => { setShowIncomeTaxMenu(false); navigate('/'); }}
                        className="text-[12px] text-teal-400 hover:text-teal-300 font-medium underline underline-offset-4 decoration-teal-500/40"
                        data-testid="sim-view-all"
                      >
                        View All Simulators
                      </button>
                    </div>
                  </div>
                </>
              )}
            </div>
            
            {/* Workshops - highlighted */}
            <button
              onClick={() => {
                if (location.pathname === '/') {
                  const el = document.getElementById('upcoming-workshops');
                  if (el) { el.scrollIntoView({ behavior: 'smooth' }); return; }
                }
                navigate('/#upcoming-workshops');
              }}
              className="hidden lg:flex items-center gap-2 px-3 py-2 rounded-lg text-sm font-medium whitespace-nowrap transition-all bg-gradient-to-r from-teal-500/10 to-cyan-500/10 border border-teal-500/40 hover:from-teal-500/20 hover:to-cyan-500/20"
              data-testid="nav-workshops-link"
              title="Live Workshops"
            >
              <Video className="w-4 h-4 text-teal-400" />
              <span className="text-teal-400">Workshops</span>
              <span className="text-[9px] font-bold bg-rose-500/20 text-rose-300 border border-rose-500/40 px-1.5 py-0.5 rounded animate-pulse">LIVE</span>
            </button>

            {/* For Business - Highlighted */}
            <button 
              onClick={() => navigate('/for-business')}
              className={`flex items-center gap-2 px-3 py-2 rounded-lg text-sm font-medium whitespace-nowrap transition-all bg-gradient-to-r from-amber-500/10 to-orange-500/10 border border-amber-500/40 hover:from-amber-500/20 hover:to-orange-500/20 ${location.pathname === '/for-business' ? 'from-amber-500/20 to-orange-500/20' : ''}`}
            >
              <Building2 className="w-4 h-4 text-amber-400" />
              <span className="text-amber-400">For Business</span>
            </button>

            {/* Parent Brand External Link */}
            <a
              href="https://www.taxsetu.ai"
              target="_blank"
              rel="noopener noreferrer"
              className="hidden xl:flex items-center gap-1.5 px-2.5 py-2 rounded-lg text-sm text-gray-500 hover:text-teal-400 hover:bg-white/[0.03] transition-all whitespace-nowrap"
              data-testid="nav-taxsetu-link"
              title="Visit parent company"
            >
              <span>TaxSetu AI</span>
              <ExternalLink className="w-3.5 h-3.5" />
            </a>

            {/* Divider */}
            <div className="w-px h-7 bg-white/10 mx-1"></div>

            {/* Feedback - Next to Login */}
            <button 
              onClick={() => setShowBetaModal(true)}
              className="flex items-center gap-2 px-3 py-2 rounded-lg text-sm whitespace-nowrap bg-gradient-to-r from-emerald-500/10 to-teal-500/10 border border-emerald-500/40 hover:from-emerald-500/20 hover:to-teal-500/20 transition-all"
            >
              <Gift className="w-4 h-4 text-emerald-400" />
              <span className="text-emerald-400 font-medium">Feedback</span>
            </button>

            {/* Login/User */}
            {isLoggedIn || hasUnlockedModules || leadName ? (
              <div className="relative">
                <button 
                  onClick={() => setShowUserMenu(!showUserMenu)}
                  className="flex items-center gap-2 px-3 py-2 rounded-lg whitespace-nowrap bg-gradient-to-r from-purple-500/10 to-pink-500/10 border border-purple-500/40 hover:from-purple-500/20 hover:to-pink-500/20 transition-all"
                >
                  {isAdmin ? (
                    <>
                      <Crown className="w-4 h-4 text-amber-400" />
                      <span className="text-amber-400 font-medium text-sm">Admin</span>
                    </>
                  ) : leadName ? (
                    <>
                      <User className="w-4 h-4 text-teal-400" />
                      <span className="text-teal-400 font-medium text-sm">{leadName.split(' ')[0]}</span>
                    </>
                  ) : (
                    <>
                      <User className="w-4 h-4 text-purple-400" />
                      <span className="text-purple-400 font-medium text-sm">
                        {isGstr1Paid && isGstr3bPaid ? 'Pro User' : 'User'}
                      </span>
                    </>
                  )}
                  <ChevronDown className={`w-4 h-4 text-gray-400 transition-transform ${showUserMenu ? 'rotate-180' : ''}`} />
                </button>

                {/* Dropdown Menu */}
                {showUserMenu && (
                  <>
                    <div 
                      className="fixed inset-0 z-40" 
                      onClick={() => setShowUserMenu(false)}
                    />
                    <div className="absolute right-0 top-full mt-2 w-56 bg-[#1B2838] border border-white/10 rounded-xl shadow-xl z-50 overflow-hidden">
                      {/* User Info */}
                      <div className="px-4 py-3 border-b border-white/10">
                        <p className="text-xs text-gray-500">Logged in as</p>
                        <p className="text-sm text-white font-medium truncate">
                          {leadName || user?.username || user?.email || (isAdmin ? 'Admin' : 'Demo User')}
                        </p>
                        {leadEmail && !isAdmin && (
                          <p className="text-xs text-gray-400 truncate mt-0.5">{leadEmail}</p>
                        )}
                        {isAdmin && (
                          <Badge className="mt-2 bg-amber-500/20 text-amber-400 border-amber-500/30 text-xs">
                            <Crown className="w-3 h-3 mr-1" />
                            Full Access
                          </Badge>
                        )}
                      </div>
                      
                      {/* Menu Items */}
                      <div className="py-2">
                        <button
                          onClick={() => {
                            setShowUserMenu(false);
                            navigate('/gst');
                          }}
                          className="w-full px-4 py-2.5 text-left text-sm text-gray-300 hover:bg-white/5 flex items-center gap-3"
                        >
                          <FileText className="w-4 h-4 text-teal-400" />
                          GST Dashboard
                        </button>
                      </div>
                      
                      {/* Logout */}
                      <div className="border-t border-white/10 py-2">
                        <button
                          onClick={() => {
                            setShowUserMenu(false);
                            handleLogout();
                          }}
                          className="w-full px-4 py-2.5 text-left text-sm text-red-400 hover:bg-red-500/10 flex items-center gap-3"
                        >
                          <LogOut className="w-4 h-4" />
                          Logout
                        </button>
                      </div>
                    </div>
                  </>
                )}
              </div>
            ) : (
              <Button
                onClick={() => navigate('/portal/login')}
                className="bg-gradient-to-r from-teal-500 to-cyan-500 text-white hover:from-teal-400 hover:to-cyan-400 border-0 shadow-lg shadow-teal-500/20 h-10 px-4 text-sm whitespace-nowrap"
              >
                <User className="w-4 h-4 mr-1.5" />
                Login
              </Button>
            )}
          </div>
        </div>
      </div>

      {/* Beta Founders Modal */}
      <BetaFoundersModal 
        isOpen={showBetaModal} 
        onClose={() => setShowBetaModal(false)} 
      />
    </header>
  );
};

export default Header;
